/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ViewModel } from "../../viewmodel/viewmodel"
import type { PageApplicationItem } from "../application/application-types"

import type { ApplicationDetailService } from "./application-detail-service"
import type { ApplicationDetailStore } from "./application-detail-store"
import type { ApplicationApk, ApplicationDetail, ApplicationEvent, ApplicationFlag } from "./application-detail-types"

/**
 * View model for one application detail page.
 *
 * The view model is scoped to a single application ID, so its store and service
 * calls always operate on that application. Create another instance when
 * another application needs to be loaded; the instances are lightweight and
 * contain only the state and operations needed by the detail page.
 */
export class ApplicationDetailViewModel implements ViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly applicationId: number,
        private readonly store: ApplicationDetailStore,
        private readonly service: ApplicationDetailService
    ) {}

    get applicationDetail(): ApplicationDetail | null {
        return this.store.detail
    }

    get applicationEvents(): ApplicationEvent[] {
        return Array.from(this.store.events.values()).flatMap((page) => page.content ?? [])
    }

    get applicationFlagHistory(): ApplicationFlag[] {
        return Array.from(this.store.flags.values()).flatMap((page) => page.content ?? [])
    }

    get flagsLoaded(): boolean {
        return this.store.flagsLoaded
    }

    get eventsLoaded(): boolean {
        return this.store.eventsLoaded
    }

    /** APKs still on the device, or an empty list until loaded. */
    get applicationApks(): ApplicationApk[] {
        return this.store.activeApks
    }

    /** APKs removed from the device, or an empty list until loaded. */
    get removedApplicationApks(): ApplicationApk[] {
        return this.store.removedApks
    }

    setPrefetchedAppDetail(detail: PageApplicationItem): void {
        this.store.setPrefetchedDetail(detail)
    }

    async getApplicationDetail(): Promise<void> {
        await this.service.getApplicationDetail(this.applicationId)
    }

    async getApplicationFlagHistory(): Promise<void> {
        await this.service.getApplicationFlagHistory(this.applicationId, 0)
    }

    async appendApplicationFlagHistory(): Promise<void> {
        const nextIndex = this.store.currentFlagsPageIndex + 1
        await this.service.getApplicationFlagHistory(this.applicationId, nextIndex)
    }

    async getApplicationEvents(): Promise<void> {
        await this.service.getApplicationEvents(this.applicationId, 0)
    }

    async appendApplicationEvent(): Promise<void> {
        const nextIndex = this.store.currentEventsPageIndex + 1
        await this.service.getApplicationEvents(this.applicationId, nextIndex)
    }

    // The APK endpoint uses `avUserId`, which is available after the detail loads.
    async getApplicationApks(): Promise<void> {
        const avUserId = this.store.detail?.avUserId
        if (!avUserId) return

        await this.service.getApplicationApks(avUserId)
    }
}
